/**
 * Post-purchase PRODUCT subsection helpers (editor, preview, extension).
 * Schema defaults (DESCRIPTION block, single PRICE) live in DB templates.
 */
import { isSectionVisible } from "./section-visibility.js";

export const DESCRIPTION_LAYOUT = Object.freeze({
  COLLAPSIBLE: "collapsible",
  EXPANDED: "expanded",
});

export const DESCRIPTION_LAYOUT_OPTIONS = Object.freeze([
  { value: DESCRIPTION_LAYOUT.COLLAPSIBLE, label: "Collapsible" },
  { value: DESCRIPTION_LAYOUT.EXPANDED, label: "Expanded text" },
]);

export const DESCRIPTION_SOURCE = Object.freeze({
  PRODUCT_DEFAULT: "product_default",
  CUSTOM: "custom",
});

export const DESCRIPTION_SOURCE_OPTIONS = Object.freeze([
  { value: DESCRIPTION_SOURCE.CUSTOM, label: "Custom text" },
  { value: DESCRIPTION_SOURCE.PRODUCT_DEFAULT, label: "Product default description" },
]);

/** Editor layout cards (storefront / draggable popups only). */
export const DESCRIPTION_LAYOUT_CARDS = Object.freeze([
  {
    id: DESCRIPTION_LAYOUT.COLLAPSIBLE,
    title: "Collapsible",
    description: "Label with chevron toggle",
    preview: "description-collapsible",
  },
  {
    id: DESCRIPTION_LAYOUT.EXPANDED,
    title: "Expanded text",
    description: "Always show description",
    preview: "description-expanded",
  },
]);

export function normalizeDescriptionLayout(layout) {
  const raw = String(layout || "").trim().toLowerCase();
  if (raw === DESCRIPTION_LAYOUT.EXPANDED) return DESCRIPTION_LAYOUT.EXPANDED;
  return DESCRIPTION_LAYOUT.COLLAPSIBLE;
}

export function normalizeDescriptionSource(source, sectionText = "") {
  const raw = String(source || "").trim().toLowerCase();
  if (raw === DESCRIPTION_SOURCE.PRODUCT_DEFAULT) return DESCRIPTION_SOURCE.PRODUCT_DEFAULT;
  if (raw === DESCRIPTION_SOURCE.CUSTOM) return DESCRIPTION_SOURCE.CUSTOM;
  if (/\{\{\s*description\s*\}\}/i.test(String(sectionText || ""))) {
    return DESCRIPTION_SOURCE.PRODUCT_DEFAULT;
  }
  return DESCRIPTION_SOURCE.CUSTOM;
}

export function getDescriptionDisplay(section) {
  const display = section?.setting?.display || {};
  return {
    layout: normalizeDescriptionLayout(display.descriptionLayout),
    descriptionSource: normalizeDescriptionSource(
      display.descriptionSource,
      section?.text
    ),
    collapsibleLabel:
      String(display.collapsibleLabel || "Description").trim() || "Description",
    defaultExpanded: display.defaultExpanded === true,
  };
}

export function stripHtmlToPlainText(value) {
  if (!value || typeof value !== "string") return "";
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function hasProductDescription(product) {
  if (!product) return false;
  const raw = product.body_html || product.description || "";
  return stripHtmlToPlainText(raw).length > 0;
}

function resolveCustomDescriptionText(sectionText) {
  return String(sectionText || "")
    .replace(/\{\{\s*description\s*\}\}/gi, "")
    .trim();
}

export function resolveProductDescriptionText(product, section) {
  const display = getDescriptionDisplay(section);
  const fromProduct = product?.body_html || product?.description || "";
  const sectionText = String(section?.text || "").trim();

  if (display.layout === DESCRIPTION_LAYOUT.COLLAPSIBLE) {
    if (display.descriptionSource === DESCRIPTION_SOURCE.CUSTOM) {
      return resolveCustomDescriptionText(sectionText);
    }
    return fromProduct || "";
  }

  if (/\{\{\s*description\s*\}\}/i.test(sectionText)) {
    return fromProduct || "";
  }
  return sectionText || fromProduct || "";
}

export function isRichPriceSubsection(subsection) {
  if (!subsection || subsection.type !== "PRICE") return false;
  const content = String(subsection.content || "");
  return (
    /<[^>]+>/.test(content) ||
    Boolean(String(subsection.setting?.overrideCss?.desktop || "").trim())
  );
}

/**
 * Pick the best PRODUCT subsection for a type (handles duplicate PRICE blocks).
 */
export function findProductSubsectionByType(sections, type, { visibleOnly = false } = {}) {
  if (!Array.isArray(sections)) return null;

  if (type === "PRICE") {
    const matches = sections
      .map((section, index) => ({ section, index }))
      .filter(({ section }) => section?.type === "PRICE");
    if (!matches.length) return null;

    const pool = visibleOnly
      ? matches.filter(({ section }) => isSectionVisible(section))
      : matches;
    const candidates = pool.length ? pool : matches;
    const rich = candidates.filter(({ section }) => isRichPriceSubsection(section));
    return rich.length ? rich[rich.length - 1] : candidates[candidates.length - 1];
  }

  const index = sections.findIndex((s) => s?.type === type);
  if (index < 0) return null;
  const section = sections[index];
  if (visibleOnly && !isSectionVisible(section)) return null;
  return { section, index };
}
